import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { StandardProvider } from './contexts/StandardContext';
import { OnboardingProvider } from './contexts/OnboardingContext';

import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Loading from './components/Loading';
import { PORTAL_CONFIG, ROLE_HOME_FALLBACK, resolvePortalKey } from './config/navigation';

// Pages
import DocumentsPage from './pages/DocumentsPage';
const PublicSharePage = React.lazy(() => import('./pages/PublicSharePage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));

const TasksPage = React.lazy(() => import('./pages/TasksPage'));
const AuditsPage = React.lazy(() => import('./pages/AuditsPage'));
const CAPAPage = React.lazy(() => import('./pages/CAPAPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const ActivityLogsPage = React.lazy(() => import('./pages/ActivityLogsPage'));
const TaskTemplatesPage = React.lazy(() => import('./pages/TaskTemplatesPage'));
const ISOClauseMappingPage = React.lazy(() => import('./pages/ISOClauseMappingPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const DepartmentsPage = React.lazy(() => import('./pages/DepartmentsPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));
const OrganizationalChartPage = React.lazy(() => import('./pages/OrganizationalChartPage'));
const TrainingManagementPage = React.lazy(() => import('./pages/TrainingManagementPage'));
const TrainingPage = React.lazy(() => import('./pages/TrainingPage'));
const RiskRegisterPage = React.lazy(() => import('./pages/RiskRegisterPage'));
const SuppliersPage = React.lazy(() => import('./pages/SuppliersPage'));
const NonConformitiesPage = React.lazy(() => import('./pages/NonConformitiesPage'));
const CustomerComplaintsPage = React.lazy(() => import('./pages/CustomerComplaintsPage'));
const QualityObjectivesPage = React.lazy(() => import('./pages/QualityObjectivesPage'));
const EquipmentPage = React.lazy(() => import('./pages/EquipmentPage'));
const ManagementReviewPage = React.lazy(() => import('./pages/ManagementReviewPage'));
const ChangeManagementPage = React.lazy(() => import('./pages/ChangeManagementPage'));
const QualityAlertsPage = React.lazy(() => import('./pages/QualityAlertsPage'));
const AnonymousReportingPage = React.lazy(() => import('./pages/AnonymousReportingPage'));
const EnvironmentalAspectsPage = React.lazy(() => import('./pages/EnvironmentalAspectsPage'));
const SafetyIncidentsPage = React.lazy(() => import('./pages/SafetyIncidentsPage'));
const HazardRegisterPage = React.lazy(() => import('./pages/HazardRegisterPage'));
const AssetRegisterPage = React.lazy(() => import('./pages/AssetRegisterPage'));
const ThreatIntelligencePage = React.lazy(() => import('./pages/ThreatIntelligencePage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage'));
const JobPositionsPage = React.lazy(() => import('./pages/jobs/JobPositionsPage'));
const JobDescriptionsPage = React.lazy(() => import('./pages/jobs/JobDescriptionsPage'));
const JobPlaceholderPage = React.lazy(() => import('./pages/jobs/JobPlaceholderPage'));
const JobLibrariesPage = React.lazy(() => import('./pages/jobs/JobLibrariesPage'));
const JobSettingsPage = React.lazy(() => import('./pages/jobs/JobSettingsPage'));
const JobAnalyticsPage = React.lazy(() => import('./pages/jobs/JobAnalyticsPage'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const PermissionsPage = React.lazy(() => import('./pages/PermissionsPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const ComplianceGapsPage = React.lazy(() => import('./pages/ComplianceGapsPage'));
const SupportTicketsPage = React.lazy(() => import('./pages/SupportTicketsPage'));
const EmployeeChecklistPage = React.lazy(() => import('./pages/EmployeeChecklistPage'));
const PublicReportPage = React.lazy(() => import('./pages/PublicReportPage'));



function RoleLandingRedirect() {
  const { user } = useAuth();
  const portalKey = resolvePortalKey(user);
  const portalConfig = PORTAL_CONFIG[portalKey] || PORTAL_CONFIG.default;
  const target = portalConfig.homeRoute || ROLE_HOME_FALLBACK;
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <OnboardingProvider>
            <React.Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/share/:token" element={<PublicSharePage />} />
                <Route path="/public-report/:orgId" element={<PublicReportPage />} />
                <Route
                  path="/app"
                  element={
                    <PrivateRoute>
                      <TenantProvider>
                        <StandardProvider>
                          <Layout />
                        </StandardProvider>
                      </TenantProvider>
                    </PrivateRoute>
                  }
                >
                  <Route index element={<RoleLandingRedirect />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="documents" element={<DocumentsPage />} />
                  <Route path="process-improvement" element={<TasksPage />} />
                  <Route path="audits" element={<AuditsPage />} />
                  <Route path="capa" element={<CAPAPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="employee-checklist" element={<EmployeeChecklistPage />} />
                  <Route path="permissions" element={<PermissionsPage />} />
                  <Route path="activity-logs" element={<ActivityLogsPage />} />
                  <Route path="task-templates" element={<TaskTemplatesPage />} />
                  <Route path="iso-clauses" element={<ISOClauseMappingPage />} />
                  <Route path="org-processes" element={<DepartmentsPage />} />
                  <Route path="departments" element={<TeamsPage />} />
                  <Route path="organizational-chart" element={<OrganizationalChartPage />} />
                  <Route path="risks" element={<RiskRegisterPage />} />
                  <Route path="suppliers" element={<SuppliersPage />} />
                  <Route path="nonconformities" element={<NonConformitiesPage />} />
                  <Route path="complaints" element={<CustomerComplaintsPage />} />
                  <Route path="objectives" element={<QualityObjectivesPage />} />
                  <Route path="equipment" element={<EquipmentPage />} />
                  <Route path="management-reviews" element={<ManagementReviewPage />} />
                  <Route path="change-management" element={<ChangeManagementPage />} />
                  <Route path="quality-alerts" element={<QualityAlertsPage />} />
                  <Route path="anonymous-reports" element={<AnonymousReportingPage />} />
                  <Route path="environmental-aspects" element={<EnvironmentalAspectsPage />} />
                  <Route path="safety-incidents" element={<SafetyIncidentsPage />} />
                  <Route path="hazards" element={<HazardRegisterPage />} />
                  <Route path="assets" element={<AssetRegisterPage />} />
                  <Route path="threat-intelligence" element={<ThreatIntelligencePage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="training-management" element={<TrainingManagementPage />} />
                  <Route path="training" element={<TrainingPage />} />
                  <Route path="training/:id" element={<TrainingPage />} />
                  <Route path="onboarding" element={<OnboardingPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />

                  {/* Job Module Routes */}
                  <Route path="jobs" element={<Navigate to="jobs/positions" replace />} />
                  <Route path="jobs/positions" element={<JobPositionsPage />} />
                  <Route path="jobs/descriptions" element={<JobDescriptionsPage />} />
                  <Route path="jobs/libraries" element={<JobLibrariesPage />} />
                  <Route path="jobs/analytics" element={<JobAnalyticsPage />} />
                  <Route path="jobs/settings" element={<JobSettingsPage />} />


                  <Route path="compliance/frameworks" element={<ComplianceGapsPage />} />
                  <Route path="support-tickets" element={<SupportTicketsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>
              </Routes>
            </React.Suspense>
          </OnboardingProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;

